import { envEnum } from '@/types/env.enum';

export function getOrganizationLinks(urlSlug: string, isV2OrderFormEnabled?: boolean) {
  const baseUrl = `/organizations/${urlSlug || ':organizationUrlSlug'}`;

  return {
    batches: {
      new: `${baseUrl}/batches/new`,
      list: `${baseUrl}/batches`,
      edit: (id: string) => `${baseUrl}/batches/${id}`,
    },
    products: {
      new: `${baseUrl}/products/new`,
      list: `${baseUrl}/products`,
      edit: (id: string) => `${baseUrl}/products/${id}`,
    },
    skus: {
      new: `${baseUrl}/skus/new`,
      list: `${baseUrl}/skus`,
      edit: (id: string) => `${baseUrl}/skus/${id}`,
    },
    organization: {
      detail: baseUrl,
      settings: `${baseUrl}/settings`,
    },
    orders: {
      new: isV2OrderFormEnabled ? `${baseUrl}/orders/new/v2` : `${baseUrl}/orders/new`,
      // Some order-form related functionality requires the legacy order form rather than v2
      newLegacy: `${baseUrl}/orders/new`,
      newV2: `${baseUrl}/orders/new/v2`,
      list: `${baseUrl}/orders`,
      adminList: `${baseUrl}/admin-orders`,
      edit: (orderId: string, urlSlugOverride?: string, isV2OrderFormEnabledOverride?: boolean) => {
        const baseUrlToUse = urlSlugOverride ? `/organizations/${urlSlugOverride}` : baseUrl;

        return isV2OrderFormEnabledOverride ?? isV2OrderFormEnabled
          ? `${baseUrlToUse}/orders/${orderId}/v2`
          : `${baseUrlToUse}/orders/${orderId}`;
      },
      duplicate: (orderId: string, urlSlugOverride?: string, useLegacyForm?: boolean) => {
        const baseUrlToUse = urlSlugOverride ? `/organizations/${urlSlugOverride}` : baseUrl;

        const legacyFormPath = `${baseUrlToUse}/orders/new?source=${orderId}`;

        if (useLegacyForm) {
          return legacyFormPath;
        }

        return isV2OrderFormEnabled ? `${baseUrlToUse}/orders/new/v2?copyFromOrderId=${orderId}` : legacyFormPath;
      },
    },
    drafts: {
      list: `${baseUrl}/drafts`,
      duplicate: (draftId: string, urlSlugOverride?: string) => {
        const baseUrlToUse = urlSlugOverride ? `/organizations/${urlSlugOverride}` : baseUrl;

        // There is no draft duplication for legacy NY order form (so we just use the v2 form)
        return `${baseUrlToUse}/orders/new/v2?copyFromDraftId=${draftId}`;
      },
    },
    users: {
      list: `${baseUrl}/users`,
      adminList: `${baseUrl}/admin-users`,
      edit: (id: string) => `${baseUrl}/users?userId=${id}`,
      adminEdit: (id: string) => `${baseUrl}/admin-users?userId=${id}`,
    },
    licenses: {
      list: `${baseUrl}/licenses`,
    },
    remittances: {
      list: `${baseUrl}/admin-remittances`,
    },
    accounts: {
      list: `${baseUrl}/accounts`,
      edit: (id: string) => `${baseUrl}/accounts/${id}`,
      new: `${baseUrl}/accounts/new`,
    },
    balances: {
      list: `${baseUrl}/balances`,
    },
    shipments: {
      list: `${baseUrl}/shipments`,
    },
    zipcodes: {
      list: `${baseUrl}/zipcodes`,
    },
    organizations: {
      adminList: `${baseUrl}/admin-organizations`,
    },
    analytics: {
      list: `${baseUrl}/analytics`,
      detailedOrders: `${baseUrl}/analytics/detailed-orders`,
    },
    accounting: {
      list: `${baseUrl}/admin-accounting`,
    },
    invoices: {
      list: `${baseUrl}/admin-invoices`,
    },
    transactions: {
      list: `${baseUrl}/admin-transactions`,
    },
    marketplace: {
      settings: `${baseUrl}/marketplace`,
    },
  };
}

export const auth = {
  login: '/login',
  logout: '/login/sign-out',
  forgotPassword: '/forgot-password',
};

export const organizationCreateLink = '/organizations/new';

export const myOrganizations = '/my-organizations';

export const home = myOrganizations;

export const getInfoplusLinks = () => {
  const subdomain = process.env.NEXT_PUBLIC_ENV === envEnum.production ? 'nabis' : 'nabis-test';

  const baseUrl = `https://${subdomain}.infopluswms.com/infoplus-wms`;

  return {
    order: (id: number) => `${baseUrl}/order/req/${id}`,
    asn: (id: number) => `${baseUrl}/purchasing/purch/${id}`,
    item: (id: number) => `${baseUrl}/item-management/item/${id}`,
  };
};

export const STATUS_PAGE_URL = 'https://status.nabis.com';
export const NY_POTENCY_TAX_DETAILS =
  'https://www.tax.ny.gov/bus/auc/#:~:text=Tax%20rates,THC%20for%20concentrated%20cannabis%3B%20and';

export const EMAILS = {
  HELP_NY: 'helpny@nabis.com',
  HELP_CA: 'help@nabis.com',
  PARTNERSHIPS: 'partnerships@nabis.com',
};

export function getRetailerLinks(slug: string) {
  const retailerUiUrl = process.env.NEXT_PUBLIC_RETAILER_UI_URL || '';
  const baseUrl = `${retailerUiUrl}/retailers/${slug || ':retailerSlug'}`;

  return {
    featured: `${baseUrl}/featured`,
  };
}
